import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  CreditCard,
  Lock,
  ShoppingCart,
  Calendar,
  Clock,
  Award,
  Loader2,
  CheckCircle,
  User,
  Building2,
  MapPin,
  Phone,
  Mail,
  Users,
  Send,
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { ParticipantForm } from '@/components/checkout/ParticipantForm';
import { RegistrationTypeSelector } from '@/components/checkout/RegistrationTypeSelector';
import { EmbeddedPaymentModal } from '@/components/checkout/EmbeddedPaymentModal';
import AddressForm from '@/components/address/AddressForm';
import { Tag, X } from 'lucide-react';

// Helper function to extract max participants from variant name
const getMaxParticipants = (variant) => {
  if (!variant) return 1;
  const v = variant.toLowerCase();
  
  // Check for group/corporate variants (up to 20)
  if (v.includes('group') || v.includes('20 participants') || v.includes('corporate')) {
    return 20;
  }
  
  // Check for specific participant counts
  const match = v.match(/(\d+)\s*participant/);
  if (match) {
    return parseInt(match[1], 10);
  }
  
  // Default to 1 participant
  return 1;
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { cart, loading: cartLoading, checkout, applyPromoCode, removePromoCode } = useCart();
  
  const [submitting, setSubmitting] = useState(false);
  const [payment, setPayment] = useState(null); // { clientSecret, publishableKey }
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [currentStep, setCurrentStep] = useState(1); // 1: Registration Type, 2: Participants, 3: Billing
  const [promoInput, setPromoInput] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  
  // Registration type: 'self', 'others', 'self_and_others'
  const [registrationType, setRegistrationType] = useState('self');
  
  // Participants data - keyed by cart item index
  const [itemParticipants, setItemParticipants] = useState({});
  
  // Send confirmation emails to participants
  const [sendEmailsToParticipants, setSendEmailsToParticipants] = useState(true);
  
  const [billingInfo, setBillingInfo] = useState({
    billing_first_name: '',
    billing_last_name: '',
    billing_email: '',
    billing_phone: '',
    billing_company: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_country: 'US',
  });

  const [shippingInfo, setShippingInfo] = useState({
    shipping_first_name: '',
    shipping_last_name: '',
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_country: 'US',
  });

  useEffect(() => {
    // Wait for auth to be checked before making redirect decisions
    if (authLoading) {
      return;
    }
    
    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout');
      return;
    }
    
    // Redirect to cart if empty (only after cart has been loaded)
    if (!cartLoading && cart.items.length === 0) {
      navigate('/cart');
    }
  }, [isAuthenticated, authLoading, cart, cartLoading, navigate]);

  useEffect(() => {
    // Pre-fill billing info with user data including address from profile
    if (user) {
      setBillingInfo(prev => ({
        ...prev,
        billing_first_name: user.first_name || '',
        billing_last_name: user.last_name || '',
        billing_email: user.email || '',
        billing_phone: user.phone || '',
        billing_company: user.company || '',
        billing_address: user.address || '',
        billing_city: user.city || '',
        billing_state: user.state || '', // Store state code directly
        billing_zip: user.zip_code || '',
        billing_country: user.country || 'US',
      }));
      
      // Also pre-fill shipping info in case user unchecks "same as billing"
      setShippingInfo(prev => ({
        ...prev,
        shipping_first_name: user.first_name || '',
        shipping_last_name: user.last_name || '',
        shipping_address: user.address || '',
        shipping_city: user.city || '',
        shipping_state: user.state || '', // Store state code directly
        shipping_zip: user.zip_code || '',
        shipping_country: user.country || 'US',
      }));
    }
  }, [user]);

  const handleBillingChange = (field, value) => {
    setBillingInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleShippingChange = (field, value) => {
    setShippingInfo(prev => ({ ...prev, [field]: value }));
  };

  // Initialize participants for each cart item based on registration type
  useEffect(() => {
    if (cart.items.length > 0) {
      const newParticipants = {};
      cart.items.forEach((item, index) => {
        const maxParts = getMaxParticipants(item.variant);
        const initialParticipant = {
          id: Date.now() + index,
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          is_self: registrationType === 'self' || registrationType === 'self_and_others',
        };
        
        // Pre-fill first participant if registering for self
        if ((registrationType === 'self' || registrationType === 'self_and_others') && user) {
          initialParticipant.first_name = user.first_name || '';
          initialParticipant.last_name = user.last_name || '';
          initialParticipant.email = user.email || '';
          initialParticipant.phone = user.phone || '';
          initialParticipant.is_self = true;
        }
        
        newParticipants[index] = [initialParticipant];
      });
      setItemParticipants(newParticipants);
    }
  }, [cart.items, registrationType, user]);

  // Calculate total max participants across all items
  const totalMaxParticipants = useMemo(() => {
    return cart.items.reduce((sum, item) => sum + getMaxParticipants(item.variant), 0);
  }, [cart.items]);

  // Check if any item requires multiple participants
  const hasMultiParticipantItems = useMemo(() => {
    return cart.items.some(item => getMaxParticipants(item.variant) > 1);
  }, [cart.items]);

  const validateParticipants = () => {
    for (const [itemIndex, participants] of Object.entries(itemParticipants)) {
      for (let i = 0; i < participants.length; i++) {
        const p = participants[i];
        if (!p.first_name?.trim() || !p.last_name?.trim() || !p.email?.trim() || !p.phone?.trim()) {
          const item = cart.items[itemIndex];
          toast.error(`Please fill in all details for Participant ${i + 1} of "${item?.title}"`);
          return false;
        }
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(p.email)) {
          toast.error(`Please enter a valid email for Participant ${i + 1}`);
          return false;
        }
      }
    }
    return true;
  };

  const validateForm = () => {
    const required = [
      'billing_first_name',
      'billing_last_name',
      'billing_email',
      'billing_address',
      'billing_city',
      'billing_state',
      'billing_zip',
    ];

    for (const field of required) {
      if (!billingInfo[field]?.trim()) {
        toast.error(`Please fill in ${field.replace('billing_', '').replace('_', ' ')}`);
        return false;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(billingInfo.billing_email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleApplyPromoOnCheckout = async () => {
    if (!promoInput.trim()) return;
    setApplyingPromo(true);
    const result = await applyPromoCode(promoInput);
    if (result.success) {
      setPromoInput('');
    }
    setApplyingPromo(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate participants first
    if (!validateParticipants()) return;
    
    if (!validateForm()) return;

    setSubmitting(true);
    
    // Build participants data for each item
    const itemsWithParticipants = cart.items.map((item, index) => ({
      ...item,
      participants: itemParticipants[index] || [],
      registration_type: registrationType,
    }));
    
    const checkoutData = {
      ...billingInfo,
      same_as_billing: sameAsBilling,
      ...(sameAsBilling ? {} : shippingInfo),
      promo_code: cart.promo_code,
      items_with_participants: itemsWithParticipants,
      send_participant_emails: sendEmailsToParticipants,
      registration_type: registrationType,
    };

    const result = await checkout(checkoutData);
    
    if (!result.success) {
      setSubmitting(false);
      if (result.error) {
        toast.error(result.error);
      }
      return;
    }

    // Payment required -> open the embedded Stripe form in a modal (stays on our domain)
    if (result.data?.payment_required && result.data?.client_secret) {
      setPayment({
        clientSecret: result.data.client_secret,
        publishableKey: result.data.publishable_key,
      });
      setSubmitting(false);
      return;
    }

    // Free / 100%-discount order completed immediately
    if (result.data?.order_id) {
      navigate(`/checkout/success?order_id=${result.data.order_id}`);
    }
    setSubmitting(false);
  };

  const formatVariant = (variant) => {
    // For legacy variants, convert to display names
    const legacyVariants = {
      'live': 'Live Access for 1 Participant',
      'recorded': 'Download Access for 1 Participant',
      'digital_download': 'USB Flash Drive for 1 Participant',
      'group_live': 'Group Live (Up to 20 Participants)',
      'live_recorded': 'Live + Download Access for 1 Participant',
      'live_usb': 'Live + USB Flash Drive for 1 Participant',
    };
    // If it's a legacy code, convert it; otherwise return as-is (full name from OpenCart or new orders)
    return legacyVariants[variant] || variant;
  };

  if (authLoading || cartLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pt-28 pb-12" data-testid="checkout-page">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-8">
          <Link to="/cart">
            <Button variant="ghost" className="text-zinc-900 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Cart
            </Button>
          </Link>
          <h1 className="font-heading text-3xl font-bold text-zinc-950">Checkout</h1>
          <p className="text-zinc-500 mt-1">Complete your order</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Side - Forms */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Step 1: Registration Type */}
              <RegistrationTypeSelector
                registrationType={registrationType}
                setRegistrationType={setRegistrationType}
                variant={cart.items[0]?.variant}
                maxParticipants={totalMaxParticipants}
              />

              {/* Step 2: Participant Details for each item */}
              {cart.items.map((item, index) => {
                const maxParts = getMaxParticipants(item.variant);
                return (
                  <ParticipantForm
                    key={item.cart_item_id || index}
                    participants={itemParticipants[index] || []}
                    setParticipants={(participants) => {
                      setItemParticipants(prev => ({
                        ...prev,
                        [index]: participants
                      }));
                    }}
                    maxParticipants={maxParts}
                    currentUser={user}
                    itemTitle={item.title}
                    variant={item.variant}
                  />
                );
              })}

              {/* Send Email to Participants Option */}
              {registrationType !== 'self' && (
                <Card className="border-zinc-200 rounded-xl">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="send-emails"
                        checked={sendEmailsToParticipants}
                        onCheckedChange={setSendEmailsToParticipants}
                      />
                      <div className="flex-1">
                        <label htmlFor="send-emails" className="text-sm font-medium text-zinc-950 cursor-pointer flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          Send registration details to participants
                        </label>
                        <p className="text-xs text-zinc-500 mt-1">
                          Each participant will receive an email with the webinar details and their registration confirmation.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Billing Information */}
              <Card className="border-zinc-200 rounded-xl">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-zinc-950 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Billing Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="billing_first_name">First Name *</Label>
                      <Input
                        id="billing_first_name"
                        value={billingInfo.billing_first_name}
                        onChange={(e) => handleBillingChange('billing_first_name', e.target.value)}
                        placeholder="John"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billing_last_name">Last Name *</Label>
                      <Input
                        id="billing_last_name"
                        value={billingInfo.billing_last_name}
                        onChange={(e) => handleBillingChange('billing_last_name', e.target.value)}
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="billing_email">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="billing_email"
                          type="email"
                          value={billingInfo.billing_email}
                          onChange={(e) => handleBillingChange('billing_email', e.target.value)}
                          placeholder="john@example.com"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billing_phone">Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="billing_phone"
                          type="tel"
                          value={billingInfo.billing_phone}
                          onChange={(e) => handleBillingChange('billing_phone', e.target.value)}
                          placeholder="(555) 123-4567"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="billing_company">Company / Organization</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="billing_company"
                        value={billingInfo.billing_company}
                        onChange={(e) => handleBillingChange('billing_company', e.target.value)}
                        placeholder="Company name"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {/* Address Fields */}
                  <AddressForm
                    data={billingInfo}
                    onChange={handleBillingChange}
                    prefix="billing_"
                  />
                </CardContent>
              </Card>

              {/* Shipping Information */}
              <Card className="border-zinc-200 rounded-xl">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-zinc-950 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Shipping Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="same_as_billing"
                      checked={sameAsBilling}
                      onCheckedChange={setSameAsBilling}
                    />
                    <label
                      htmlFor="same_as_billing"
                      className="text-sm font-medium text-zinc-700 cursor-pointer"
                    >
                      Same as billing address
                    </label>
                  </div>

                  {!sameAsBilling && (
                    <div className="space-y-4 pt-4 border-t border-zinc-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="shipping_first_name">First Name</Label>
                          <Input
                            id="shipping_first_name"
                            value={shippingInfo.shipping_first_name}
                            onChange={(e) => handleShippingChange('shipping_first_name', e.target.value)}
                            placeholder="John"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="shipping_last_name">Last Name</Label>
                          <Input
                            id="shipping_last_name"
                            value={shippingInfo.shipping_last_name}
                            onChange={(e) => handleShippingChange('shipping_last_name', e.target.value)}
                            placeholder="Doe"
                          />
                        </div>
                      </div>

                      {/* Address Fields */}
                      <AddressForm
                        data={shippingInfo}
                        onChange={handleShippingChange}
                        prefix="shipping_"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Side - Order Summary */}
            <div className="lg:col-span-1">
              <Card className="border-zinc-200 rounded-xl sticky top-28">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-zinc-950 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Cart Items */}
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {cart.items.map((item, index) => (
                      <div key={index} className="p-3 bg-zinc-50 rounded-lg">
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-zinc-950 line-clamp-2">
                              {item.title}
                            </p>
                            <p className="text-xs text-zinc-500 mt-1">
                              {formatVariant(item.variant)}
                            </p>
                            {item.speakers && item.speakers.length > 0 && (
                              <p className="text-xs text-zinc-500">
                                Speaker: {item.speakers.map(s => s.name).join(', ')}
                              </p>
                            )}
                            {item.webinar_code && (
                              <p className="text-xs text-zinc-400">
                                Code: {item.webinar_code}
                              </p>
                            )}
                            {item.quantity > 1 && (
                              <p className="text-xs text-zinc-900 font-medium mt-1">
                                Qty: {item.quantity}
                              </p>
                            )}
                          </div>
                          <p className="text-sm font-semibold text-zinc-950 whitespace-nowrap">
                            ${(item.price * (item.quantity || 1)).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  {/* Promo Code Section */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-700 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Promo Code
                    </label>
                    {cart.promo_code ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-green-700 text-sm truncate" data-testid="checkout-applied-promo-code">{cart.promo_code}</p>
                            <p className="text-xs text-green-600">
                              {cart.promo_details?.type === 'percentage'
                                ? `${cart.promo_details.value}% off`
                                : cart.promo_details?.value != null
                                  ? `$${cart.promo_details.value} off`
                                  : 'Applied'}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:text-red-600 h-8 w-8 p-0"
                          onClick={removePromoCode}
                          data-testid="checkout-remove-promo-btn"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code"
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          className="flex-1"
                          data-testid="checkout-promo-input"
                        />
                        <Button
                          type="button"
                          onClick={handleApplyPromoOnCheckout}
                          disabled={applyingPromo || !promoInput.trim()}
                          className="bg-zinc-950 hover:bg-zinc-800"
                          data-testid="checkout-apply-promo-btn"
                        >
                          {applyingPromo ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Apply'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Totals */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-zinc-600">
                      <span>Subtotal</span>
                      <span>${cart.subtotal.toFixed(2)}</span>
                    </div>
                    
                    {cart.discount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Discount ({cart.promo_code})
                        </span>
                        <span>-${cart.discount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    <Separator />
                    
                    <div className="flex justify-between text-lg font-bold text-zinc-950">
                      <span>Total</span>
                      <span>${cart.total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold py-6 text-lg rounded-xl"
                    data-testid="place-order-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        {cart.total > 0 ? 'Proceed to Payment' : 'Complete Order'}
                      </>
                    )}
                  </Button>

                  {/* Security Note */}
                  <div className="flex items-center justify-center gap-2 text-xs text-zinc-400 mt-4">
                    <Lock className="w-4 h-4" />
                    <span>256-bit SSL Encrypted Payment</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>

      <EmbeddedPaymentModal
        open={!!payment}
        onClose={() => setPayment(null)}
        clientSecret={payment?.clientSecret}
        publishableKey={payment?.publishableKey}
      />
    </div>
  );
}
